import React, { useState } from "react";
import "./landing/landing.css";
import { useAuth } from "../hooks/useAuth";

/**
 * Shared editorial header + footer for EVERY public page — the landing ("/"),
 * the SEO/guide/article/glossary pages, /pricing, /terms, /privacy, and the
 * public dashboard. One chrome everywhere so the brand never visibly swaps when
 * a visitor moves between, say, the landing and /pricing (the split that used
 * to ship two different headers/footers).
 *
 * Reuses the .lp-* editorial classes from components/landing/landing.css
 * (imported above, so the styles are present even on a standalone static page
 * where LandingPage itself isn't mounted).
 *
 * Behavior differs only by context, via optional props:
 *  - On the landing, App passes onSignIn / onGetStarted so the buttons drive the
 *    in-app AuthModal and the /app navigation through React state.
 *  - On standalone static pages (no AuthModal mounted) the buttons fall back to
 *    plain hrefs: Sign in -> /?auth=login (App opens the modal from the param),
 *    "Get started — free" -> /?auth=signup (opens the signup modal on the landing).
 *
 * Nav-link behavior forks on auth state (fix for the bug where logged-in
 * users clicking "Lead types" from /learn / /pricing / any static page
 * were dropped onto the map view instead of the intended landing section).
 * Root cause: the anchor hrefs "/#sec-…" navigate to "/", which for
 * logged-in users renders <Dashboard> (map), not <LandingPage>, so the
 * anchor never resolves. Instead of forcing LandingPage onto logged-in
 * users, we remap each nav link to its logged-in equivalent — a real page
 * that means the same thing to someone who already has an account.
 *
 *   Lead types     /#sec-categories  →  /learn (hub of lead-type explainers)
 *   Auctions       /#sec-f01         →  /app  (the actual auctions feed)
 *   How it works   /#sec-how         →  /nc-tax-foreclosure-guide (how-to article)
 *   Pricing        /pricing          →  /pricing  (unchanged — public page)
 *   FAQ            /#sec-faq         →  /glossary (glossary is the reference surface for logged-in users)
 */

const Brand = (
  <span className="lp-brand">
    <span className="lp-brand-name">Parcely</span>
    <span className="lp-brand-dot" />
  </span>
);

interface NavLink {
  href: string;
  label: string;
}

const LOGGED_OUT_NAV_LINKS: NavLink[] = [
  { href: "/#sec-categories", label: "Lead types" },
  { href: "/#sec-f01", label: "Auctions" },
  { href: "/#sec-how", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/#sec-faq", label: "FAQ" },
];

const LOGGED_IN_NAV_LINKS: NavLink[] = [
  { href: "/learn", label: "Lead types" },
  { href: "/app", label: "Auctions" },
  { href: "/nc-tax-foreclosure-guide", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/glossary", label: "FAQ" },
];

interface PublicPageHeaderProps {
  /** Landing only — open the in-app AuthModal. Omit on static pages. */
  onSignIn?: () => void;
  /** Landing only — navigate into the app via React Router. Omit on static pages. */
  onGetStarted?: () => void;
}

export const PublicPageHeader: React.FC<PublicPageHeaderProps> = ({ onSignIn, onGetStarted }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Pick the nav-link set from auth state. During Supabase's initial session
  // check (loading=true), the previous flicker was to render logged-out links
  // and then swap — reasonable enough since a logged-in user's actual dropdown
  // would still work post-hydration. Kept simple: `user` is the source of
  // truth; loading defaults to logged-out (which is what the site is anyway
  // for a first-time visitor).
  const { user } = useAuth();
  const navLinks = user ? LOGGED_IN_NAV_LINKS : LOGGED_OUT_NAV_LINKS;

  const signIn = onSignIn ? (
    <button className="lp-btn-ghost" onClick={onSignIn} style={{ fontSize: 13 }}>
      Sign in
    </button>
  ) : (
    <a className="lp-btn-ghost" href="/?auth=login" style={{ fontSize: 13, textDecoration: "none" }}>
      Sign in
    </a>
  );

  const cta = onGetStarted ? (
    <button className="lp-nav-cta" onClick={onGetStarted}>
      Get started — free
    </button>
  ) : (
    <a className="lp-nav-cta" href="/?auth=signup" style={{ textDecoration: "none" }}>
      Get started — free
    </a>
  );

  const mobileCta = onGetStarted ? (
    <button className="cta" onClick={onGetStarted}>
      Get started — free
    </button>
  ) : (
    <a className="cta" href="/?auth=signup" style={{ textDecoration: "none" }}>
      Get started — free
    </a>
  );

  return (
    <>
    {/* Skip link (#211): first focusable element on every public page. Targets id="main" on each page's content wrapper. */}
    <a className="skip-link" href="#main">Skip to main content</a>
    <header className="lp-nav" id="sec-header">
      <div className="lp-nav-inner">
        <a href="/#sec-hero" aria-label="Parcely home">
          {Brand}
        </a>
        <nav className="lp-nav-links">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
        </nav>
        <div className="lp-nav-right">
          {signIn}
          {cta}
          <button className="lp-hamburger" aria-label="Menu" onClick={() => setMobileOpen((v) => !v)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#16202E" strokeWidth="1.4">
              <path d="M1 4h14M1 8h14M1 12h14" />
            </svg>
          </button>
        </div>
      </div>
      <div className={"lp-mobile-menu" + (mobileOpen ? " open" : "")}>
        <nav onClick={() => setMobileOpen(false)}>
          {navLinks.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
          {mobileCta}
        </nav>
      </div>
    </header>
    </>
  );
};

// Pre-filled prompt for the "Ask AI" footer links (a GEO/brand nudge). Static,
// so it lives at module scope rather than inside the component.
const askAiPrompt = encodeURIComponent(
  "What is Parcely (parcelyapp.com), and how does it help investors find off-market property leads in North Carolina?"
);

export const PublicPageFooter: React.FC = () => (
  <footer id="sec-footer" className="lp-footer">
    <div className="lp-footer-top">
      <div className="lp-footer-brand">
        <a href="/#sec-hero" aria-label="Parcely home">
          {Brand}
        </a>
        <p>Off-market property leads from the North Carolina public record.</p>
        <p className="lp-footer-copy">© {new Date().getFullYear()} Parcely LLC</p>
      </div>
      <div className="lp-footer-cols lp-footer-cols--4">
        <div>
          <p className="lp-footer-h">Product</p>
          <ul>
            <li><a href="/#sec-categories">Lead types</a></li>
            <li><a href="/#sec-f04">Owner leads</a></li>
            <li><a href="/#sec-f05">Alerts</a></li>
            <li><a href="/pricing">Pricing</a></li>
          </ul>
        </div>
        <div>
          <p className="lp-footer-h">Guides</p>
          <ul>
            <li><a href="/#sec-how">How it works</a></li>
            <li><a href="/upset-bid-calculator">Upset-bid calculator</a></li>
            <li><a href="/glossary">Glossary</a></li>
            <li><a href="/learn">Learn</a></li>
            <li><a href="/public">Coverage</a></li>
          </ul>
        </div>
        <div>
          <p className="lp-footer-h">Legal</p>
          <ul>
            <li><a href="/terms">Terms</a></li>
            <li><a href="/privacy">Privacy</a></li>
            <li><a href="/#sec-record">Data sources</a></li>
          </ul>
        </div>
      </div>
    </div>
    <div className="lp-footer-ai">
      <span className="lp-footer-ai-label">Ask AI about Parcely</span>
      <a href={`https://chatgpt.com/?q=${askAiPrompt}`} target="_blank" rel="noopener noreferrer">ChatGPT</a>
      <a href={`https://claude.ai/new?q=${askAiPrompt}`} target="_blank" rel="noopener noreferrer">Claude</a>
      <a href={`https://www.perplexity.ai/search?q=${askAiPrompt}`} target="_blank" rel="noopener noreferrer">Perplexity</a>
      <a href={`https://gemini.google.com/app?q=${askAiPrompt}`} target="_blank" rel="noopener noreferrer">Gemini</a>
      <a href={`https://grok.com/?q=${askAiPrompt}`} target="_blank" rel="noopener noreferrer">Grok</a>
    </div>
    <div className="lp-footer-disc">
      <div className="lp-footer-disc-inner">
        <p>
          <b>Not affiliated with any government entity.</b> Parcely is an independent information service. It
          is not affiliated with, endorsed by, or acting on behalf of any North Carolina county, the
          Administrative Office of the Courts, or any other government body. Information is aggregated from
          public records for research purposes only — it is not legal, financial, tax, or investment advice.
          Assessed values are not appraisals. Verify every parcel, balance, and deadline with the county of
          record before bidding.
        </p>
      </div>
    </div>
  </footer>
);
